// The Wodi patients CSV format, `wodi-patients-csv-v1` (docs/plans/phase1-importers.md §3).
//
// One row is one person from another system. The row validator here is pure and checks
// only what one row can show. The importer kind does the rest against the database: the
// tenant's sex and relationship codes, its required fields (with
// `patientRequiredFieldIssues`), identifiers already in use, and possible duplicates,
// using the same rules as the registration screen (patient-matching.ts).
//
// Row content is personal data. Issues carry column keys and codes only, never values.

import { isoDay, oneOf, optional, text, withDefault, yesNo } from '../csv/cells.ts';
import type { ColumnSpec, RowCells, RowCheck, RowIssue } from '../csv/rows.ts';
import { readCells } from '../csv/rows.ts';
import { MAX_ESTIMATED_AGE_YEARS } from '../estimated-birth-date.ts';
import type { IdentifierSystem } from '../identifiers.ts';
import { isTenantUniqueIdentifierSystem, normaliseIdentifier } from '../identifiers.ts';
import { withinYears } from '../patient-matching.ts';
import type { Msisdn } from '../phone.ts';
import { normalisePhone } from '../phone.ts';
import type { CalendarDay } from '../time.ts';
import { compareDays } from '../time.ts';
import { blankToNull, hasErrors, refuse, take } from './common.ts';

export const WODI_PATIENTS_FORMAT = 'wodi-patients-csv-v1';

export type PatientColumn =
  | 'legacy_number'
  | 'given_name'
  | 'middle_name'
  | 'family_name'
  | 'sex'
  | 'birth_date'
  | 'birth_date_estimated'
  | 'phone'
  | 'phone_belongs_to'
  | 'national_id'
  | 'birth_certificate'
  | 'alien_id'
  | 'refugee_id'
  | 'passport_number'
  | 'passport_country'
  | 'locality'
  | 'next_of_kin_given_name'
  | 'next_of_kin_family_name'
  | 'next_of_kin_phone'
  | 'next_of_kin_relationship';

export const PATIENT_COLUMNS: readonly (ColumnSpec<PatientColumn> & {
  readonly description: string;
})[] = [
  {
    key: 'legacy_number',
    required: true,
    description:
      'The patient’s number in the old system. Unique in the file. Kept so staff can still search by it.',
  },
  { key: 'given_name', required: true, description: 'Given name, up to 100 characters.' },
  { key: 'middle_name', required: false, description: 'Middle name, up to 100 characters.' },
  { key: 'family_name', required: false, description: 'Family name, up to 100 characters.' },
  {
    key: 'sex',
    required: true,
    description: 'A sex code from this organisation’s list, as shown in Settings.',
  },
  { key: 'birth_date', required: true, description: 'Date of birth, YYYY-MM-DD.' },
  {
    key: 'birth_date_estimated',
    required: false,
    description: 'yes if the date of birth is an estimate; no (default) if it is known.',
  },
  { key: 'phone', required: false, description: 'Kenyan mobile number, such as 0712345678.' },
  {
    key: 'phone_belongs_to',
    required: false,
    description: 'patient (default), or someone_else when it is a relative’s phone.',
  },
  { key: 'national_id', required: false, description: 'National ID number.' },
  { key: 'birth_certificate', required: false, description: 'Birth certificate number.' },
  { key: 'alien_id', required: false, description: 'Alien ID number.' },
  { key: 'refugee_id', required: false, description: 'Refugee ID number.' },
  { key: 'passport_number', required: false, description: 'Passport number.' },
  {
    key: 'passport_country',
    required: false,
    description:
      'The passport’s issuing country as two letters, such as KE. Needed with a passport number.',
  },
  { key: 'locality', required: false, description: 'Village or estate, up to 200 characters.' },
  { key: 'next_of_kin_given_name', required: false, description: 'Next of kin’s given name.' },
  { key: 'next_of_kin_family_name', required: false, description: 'Next of kin’s family name.' },
  { key: 'next_of_kin_phone', required: false, description: 'Next of kin’s Kenyan mobile number.' },
  {
    key: 'next_of_kin_relationship',
    required: false,
    description: 'A relationship code from this organisation’s list. Needed with a next of kin.',
  },
];

export const MAX_NAME_LENGTH = 100;
export const MAX_LOCALITY_LENGTH = 200;
/** Long enough for any value-set code; the kind checks the code exists. */
export const MAX_VALUE_SET_CODE_LENGTH = 64;

export interface ImportedIdentifier {
  readonly system: IdentifierSystem;
  /** As written in the file, trimmed. */
  readonly value: string;
  /** From `normaliseIdentifier`: what exact matches compare. */
  readonly valueNormalised: string;
  /** Passports only. */
  readonly issuingCountry: string | null;
}

export interface PatientImportRow {
  /** The `legacy_patient_number` identifier; also in `identifiers`. */
  readonly legacyNumber: ImportedIdentifier;
  readonly name: {
    readonly given: string;
    readonly middle: string | null;
    readonly family: string | null;
  };
  readonly sexCode: string;
  readonly birthDate: CalendarDay;
  readonly birthDateEstimated: boolean;
  readonly phone: { readonly value: Msisdn; readonly belongsTo: 'patient' | 'someone_else' } | null;
  /** Every identifier in the row, the legacy number first. */
  readonly identifiers: readonly ImportedIdentifier[];
  readonly locality: string | null;
  readonly nextOfKin: {
    readonly givenName: string;
    readonly familyName: string | null;
    readonly phone: Msisdn | null;
    readonly relationshipCode: string;
  } | null;
}

export type PatientRowIssueCode =
  | 'patient.passport_incomplete'
  | 'patient.invalid_country'
  | 'patient.next_of_kin_incomplete'
  | 'patient.birth_date_in_future'
  | 'patient.birth_date_too_old'
  | 'patient.required_by_settings'
  | 'patient.identifier_repeated_in_file'
  | 'patient.possible_duplicate_in_file'
  | 'patient.identifier_in_use'
  | 'patient.already_imported'
  | 'patient.possible_duplicate'
  | 'patient.unknown_code';

export const PATIENT_ROW_MESSAGES: Record<PatientRowIssueCode, string> = {
  'patient.passport_incomplete': 'A passport needs both its number and its issuing country',
  'patient.invalid_country': 'Write the issuing country as two letters, such as KE',
  'patient.next_of_kin_incomplete':
    'A next of kin needs at least a given name and a relationship code',
  'patient.birth_date_in_future': 'The date of birth is in the future',
  'patient.birth_date_too_old': `The date of birth is more than ${String(MAX_ESTIMATED_AGE_YEARS)} years ago`,
  'patient.required_by_settings':
    'This organisation requires this detail when registering a patient',
  'patient.identifier_repeated_in_file':
    'The same identifier is on another row of this file. An identifier belongs to one person.',
  'patient.possible_duplicate_in_file':
    'Another row in this file has a similar name and the same date of birth',
  'patient.identifier_in_use':
    'A patient already registered here has this identifier. Remove the row or correct the number.',
  'patient.already_imported':
    'A patient with this old-system number is already registered here. The row may have been imported before.',
  'patient.possible_duplicate': 'This may be a patient who is already registered here',
  'patient.unknown_code': 'This code is not in this organisation’s list',
};

const IDENTIFIER_COLUMNS: readonly (readonly [PatientColumn, IdentifierSystem])[] = [
  ['national_id', 'national_id'],
  ['birth_certificate', 'birth_certificate'],
  ['alien_id', 'alien_id'],
  ['refugee_id', 'refugee_id'],
];

const name = text({ maxLength: MAX_NAME_LENGTH });
const optionalName = optional(name);
const code = text({ maxLength: MAX_VALUE_SET_CODE_LENGTH });

const readers = {
  given: ['given_name', name],
  middle: ['middle_name', optionalName],
  family: ['family_name', optionalName],
  sexCode: ['sex', code],
  birthDate: ['birth_date', isoDay()],
  birthDateEstimated: ['birth_date_estimated', withDefault(yesNo(), false)],
  phoneBelongsTo: [
    'phone_belongs_to',
    withDefault(oneOf(['patient', 'someone_else'] as const), 'patient'),
  ],
  locality: ['locality', optional(text({ maxLength: MAX_LOCALITY_LENGTH }))],
  nokGiven: ['next_of_kin_given_name', optionalName],
  nokFamily: ['next_of_kin_family_name', optionalName],
  nokRelationship: ['next_of_kin_relationship', optional(code)],
} as const;

function identifier(
  issues: RowIssue[],
  column: PatientColumn,
  system: IdentifierSystem,
  raw: string,
  issuingCountry: string | null = null,
): ImportedIdentifier | null {
  const value = blankToNull(raw);
  if (value === null) return null;
  const valueNormalised = take(issues, column, normaliseIdentifier(system, value));
  return valueNormalised === null ? null : { system, value, valueNormalised, issuingCountry };
}

function phone(issues: RowIssue[], column: PatientColumn, raw: string): Msisdn | null {
  const value = blankToNull(raw);
  return value === null ? null : take(issues, column, normalisePhone(value));
}

/** Validates one row of the format: everything one row can show on its own. */
export function validatePatientRow(cells: RowCells<PatientColumn>): RowCheck<PatientImportRow> {
  const read = readCells(cells, readers);
  const issues: RowIssue[] = [...read.issues];

  const legacyText = blankToNull(cells.legacy_number);
  let legacyNumber: ImportedIdentifier | null = null;
  if (legacyText === null) refuse(issues, 'legacy_number', 'cell.required');
  else legacyNumber = identifier(issues, 'legacy_number', 'legacy_patient_number', legacyText);

  const identifiers: ImportedIdentifier[] = [];
  for (const [column, system] of IDENTIFIER_COLUMNS) {
    const found = identifier(issues, column, system, cells[column]);
    if (found !== null) identifiers.push(found);
  }

  const passportNumber = blankToNull(cells.passport_number);
  const countryText = blankToNull(cells.passport_country);
  if ((passportNumber === null) !== (countryText === null)) {
    refuse(
      issues,
      passportNumber === null ? 'passport_number' : 'passport_country',
      'patient.passport_incomplete',
    );
  } else if (passportNumber !== null && countryText !== null) {
    const country = countryText.toUpperCase();
    if (!/^[A-Z]{2}$/.test(country)) refuse(issues, 'passport_country', 'patient.invalid_country');
    else {
      const passport = identifier(issues, 'passport_number', 'passport', passportNumber, country);
      if (passport !== null) identifiers.push(passport);
    }
  }

  const patientPhone = phone(issues, 'phone', cells.phone);
  const nokPhone = phone(issues, 'next_of_kin_phone', cells.next_of_kin_phone);

  const nokColumns = [
    cells.next_of_kin_given_name,
    cells.next_of_kin_family_name,
    cells.next_of_kin_phone,
    cells.next_of_kin_relationship,
  ];
  const anyNextOfKin = nokColumns.some((c) => blankToNull(c) !== null);
  if (anyNextOfKin) {
    if (blankToNull(cells.next_of_kin_given_name) === null)
      refuse(issues, 'next_of_kin_given_name', 'patient.next_of_kin_incomplete');
    if (blankToNull(cells.next_of_kin_relationship) === null)
      refuse(issues, 'next_of_kin_relationship', 'patient.next_of_kin_incomplete');
  }

  if (read.value === null || legacyNumber === null || hasErrors(issues)) {
    return { value: null, issues };
  }
  const v = read.value;
  const nokGiven = v.nokGiven;
  const nokRelationship = v.nokRelationship;
  return {
    value: {
      legacyNumber,
      name: { given: v.given, middle: v.middle, family: v.family },
      sexCode: v.sexCode,
      birthDate: v.birthDate,
      birthDateEstimated: v.birthDateEstimated,
      phone: patientPhone === null ? null : { value: patientPhone, belongsTo: v.phoneBelongsTo },
      identifiers: [legacyNumber, ...identifiers],
      locality: v.locality,
      nextOfKin:
        anyNextOfKin && nokGiven !== null && nokRelationship !== null
          ? {
              givenName: nokGiven,
              familyName: v.nokFamily,
              phone: nokPhone,
              relationshipCode: nokRelationship,
            }
          : null,
    },
    issues,
  };
}

/**
 * Date-of-birth checks that depend on the day of the import (East Africa Time), which the
 * pure row validator doesn't know: not in the future, not more than the oldest age
 * registration accepts.
 */
export function patientBirthDateIssues(birthDate: CalendarDay, today: CalendarDay): RowIssue[] {
  if (compareDays(birthDate, today) > 0) {
    return [{ column: 'birth_date', code: 'patient.birth_date_in_future', severity: 'error' }];
  }
  if (!withinYears(birthDate, today, MAX_ESTIMATED_AGE_YEARS)) {
    return [{ column: 'birth_date', code: 'patient.birth_date_too_old', severity: 'error' }];
  }
  return [];
}

/** The details a tenant can make mandatory at registration (registration settings). */
export type RequiredRegistrationField =
  'family_name' | 'phone' | 'identifier' | 'residence' | 'next_of_kin';

const REQUIRED_FIELD_COLUMN: Record<RequiredRegistrationField, PatientColumn> = {
  family_name: 'family_name',
  phone: 'phone',
  identifier: 'national_id',
  residence: 'locality',
  next_of_kin: 'next_of_kin_given_name',
};

/**
 * The tenant's required registration fields that a row leaves out, as errors: imported
 * patients meet the same rules as patients registered at reception. The legacy number
 * doesn't count as an identifier, because it isn't a document the person holds.
 */
export function patientRequiredFieldIssues(
  row: PatientImportRow,
  required: readonly RequiredRegistrationField[],
): RowIssue[] {
  const present: Record<RequiredRegistrationField, boolean> = {
    family_name: row.name.family !== null,
    phone: row.phone !== null,
    identifier: row.identifiers.some((i) => i.system !== 'legacy_patient_number'),
    residence: row.locality !== null,
    next_of_kin: row.nextOfKin !== null,
  };
  return required
    .filter((field) => !present[field])
    .map((field) => ({
      column: REQUIRED_FIELD_COLUMN[field],
      code: 'patient.required_by_settings',
      severity: 'error' as const,
    }));
}

/** The key two rows must not share: the normalised old-system number. */
export function patientDedupeKey(row: PatientImportRow): string {
  return row.legacyNumber.valueNormalised;
}

/**
 * Keys for the row's identifiers that belong to one person in a tenant, for finding the
 * same document number on two rows. Legacy numbers are left out, as in registration.
 */
export function tenantUniqueIdentifierKeys(row: PatientImportRow): string[] {
  return row.identifiers
    .filter((i) => isTenantUniqueIdentifierSystem(i.system))
    .map((i) => `${i.system}|${i.issuingCountry ?? ''}|${i.valueNormalised}`);
}

/**
 * Row numbers whose tenant-unique identifier also appears on another row. Every row
 * involved is returned, in file order, because the file can't say which one is right.
 */
export function rowsWithRepeatedIdentifiers(
  rows: readonly (readonly [number, readonly string[]])[],
): number[] {
  const rowsByKey = new Map<string, number[]>();
  for (const [rowNumber, keys] of rows) {
    for (const key of new Set(keys)) {
      const list = rowsByKey.get(key);
      if (list === undefined) rowsByKey.set(key, [rowNumber]);
      else list.push(rowNumber);
    }
  }
  const repeated = new Set<number>();
  for (const list of rowsByKey.values()) {
    if (list.length > 1) for (const n of list) repeated.add(n);
  }
  return rows.map(([n]) => n).filter((n) => repeated.has(n));
}
